import { useEffect, useRef } from 'react'
import styles from './LoginAnimations.module.css'

interface LoginAnimationsProps {
  isVisible?: boolean
}

export function LoginAnimations({ isVisible = true }: LoginAnimationsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const mouseRef = useRef({ x: 0, y: 0 })
  const isMouseMovingRef = useRef(false)

  useEffect(() => {
    if (!isVisible) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY }
      isMouseMovingRef.current = true
      setTimeout(() => {
        isMouseMovingRef.current = false
      }, 100)
    }

    const handleMouseLeave = () => {
      isMouseMovingRef.current = false
    }

    canvas.addEventListener('mousemove', handleMouseMove)
    canvas.addEventListener('mouseleave', handleMouseLeave)

    class Particle {
      x: number
      y: number
      vx: number
      vy: number
      size: number
      opacity: number
      maxOpacity: number
      life: number
      maxLife: number
      color: string
      originalX: number
      originalY: number
      targetX: number
      targetY: number
      isRepelling: boolean
      repelStrength: number

      constructor() {
        this.x = Math.random() * (canvas?.width || 800)
        this.y = Math.random() * (canvas?.height || 600)
        this.originalX = this.x
        this.originalY = this.y
        this.targetX = this.x
        this.targetY = this.y
        this.vx = (Math.random() - 0.5) * 0.5
        this.vy = (Math.random() - 0.5) * 0.5
        this.size = Math.random() * 3 + 1
        this.maxOpacity = Math.random() * 0.8 + 0.4
        this.life = 0
        this.maxLife = Math.random() * 300 + 200
        this.color = this.getRandomColor()
        this.opacity = 0
        this.isRepelling = false
        this.repelStrength = 0
      }

      getRandomColor() {
        const colors = [
          '#4A90E2', '#7B68EE', '#20B2AA',
          '#FF6B6B', '#4ECDC4', '#45B7D1',
        ]
        return colors[Math.floor(Math.random() * colors.length)]
      }

      update() {
        this.x += this.vx
        this.y += this.vy
        this.vx += (Math.random() - 0.5) * 0.01
        this.vy += (Math.random() - 0.5) * 0.01
        this.vx = Math.max(-1, Math.min(1, this.vx))
        this.vy = Math.max(-1, Math.min(1, this.vy))

        const mouseX = mouseRef.current.x
        const mouseY = mouseRef.current.y
        const dx = this.x - mouseX
        const dy = this.y - mouseY
        const distance = Math.sqrt(dx * dx + dy * dy)
        const mouseRadius = 80

        if (distance < mouseRadius && isMouseMovingRef.current) {
          const force = (mouseRadius - distance) / mouseRadius
          const angle = Math.atan2(dy, dx)
          const repelForce = force * 1.2
          this.targetX = this.x + Math.cos(angle) * repelForce * 30
          this.targetY = this.y + Math.sin(angle) * repelForce * 30
          this.isRepelling = true
          this.repelStrength = force
        } else {
          this.targetX = this.originalX
          this.targetY = this.originalY
          this.isRepelling = false
          this.repelStrength = 0
        }

        const lerpFactor = this.isRepelling ? 0.08 : 0.02
        this.x += (this.targetX - this.x) * lerpFactor
        this.y += (this.targetY - this.y) * lerpFactor

        this.life++

        if (this.life < 60) {
          this.opacity = (this.life / 60) * this.maxOpacity
        } else if (this.life > this.maxLife - 60) {
          this.opacity = ((this.maxLife - this.life) / 60) * this.maxOpacity
        } else {
          this.opacity = this.maxOpacity
        }

        if (this.x < 0) this.x = canvas?.width || 800
        if (this.x > (canvas?.width || 800)) this.x = 0
        if (this.y < 0) this.y = canvas?.height || 600
        if (this.y > (canvas?.height || 600)) this.y = 0

        if (this.life > this.maxLife) {
          this.x = Math.random() * (canvas?.width || 800)
          this.y = Math.random() * (canvas?.height || 600)
          this.originalX = this.x
          this.originalY = this.y
          this.targetX = this.x
          this.targetY = this.y
          this.vx = (Math.random() - 0.5) * 0.5
          this.vy = (Math.random() - 0.5) * 0.5
          this.size = Math.random() * 3 + 1
          this.maxOpacity = Math.random() * 0.8 + 0.4
          this.life = 0
          this.maxLife = Math.random() * 300 + 200
          this.color = this.getRandomColor()
          this.opacity = 0
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.save()
        ctx.globalAlpha = this.opacity
        ctx.fillStyle = this.color
        ctx.shadowBlur = 10
        ctx.shadowColor = this.color
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }
    }

    class Connection {
      particle1: Particle
      particle2: Particle
      opacity: number
      maxOpacity: number

      constructor(p1: Particle, p2: Particle) {
        this.particle1 = p1
        this.particle2 = p2
        this.maxOpacity = Math.random() * 0.3 + 0.1
        this.opacity = 0
      }

      update() {
        const dx = this.particle1.x - this.particle2.x
        const dy = this.particle1.y - this.particle2.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance < 200) {
          this.opacity = this.maxOpacity * (1 - distance / 200)
        } else {
          this.opacity = 0
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.opacity > 0) {
          ctx.save()
          ctx.globalAlpha = this.opacity
          ctx.strokeStyle = '#4A90E2'
          ctx.lineWidth = 1
          ctx.shadowBlur = 5
          ctx.shadowColor = '#4A90E2'
          ctx.beginPath()
          ctx.moveTo(this.particle1.x, this.particle1.y)
          ctx.lineTo(this.particle2.x, this.particle2.y)
          ctx.stroke()
          ctx.restore()
        }
      }
    }

    const particles: Particle[] = []
    for (let i = 0; i < 80; i++) {
      particles.push(new Particle())
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas?.width || 800, canvas?.height || 600)

      particles.forEach((particle) => {
        particle.update()
        particle.draw(ctx)
      })

      const connections: Connection[] = []
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)
          if (distance < 200) {
            connections.push(new Connection(particles[i], particles[j]))
          }
        }
      }

      connections.forEach((connection) => {
        connection.update()
        connection.draw(ctx)
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', resizeCanvas)
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isVisible])

  if (!isVisible) return null

  return (
    <div className={styles.loginAnimationsContainer}>
      <canvas ref={canvasRef} className={styles.particleCanvas} />
      <div className={styles.waveContainer}>
        <div className={styles.wave}></div>
        <div className={styles.wave}></div>
        <div className={styles.wave}></div>
      </div>
      <div className={styles.neuralNetwork}>
        <div className={styles.neuralNode}></div>
        <div className={styles.neuralNode}></div>
        <div className={styles.neuralNode}></div>
        <div className={styles.neuralNode}></div>
        <div className={styles.neuralNode}></div>
      </div>
    </div>
  )
}
